import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { BrandWall, LogoPill } from "@/components/BrandWall";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BRANDS } from "@/data/site";
import { BRAND_DETAILS } from "@/data/brands";

const PageHero = () => (
  <section className="relative overflow-hidden pb-16 pt-36 sm:pb-24 sm:pt-48">
    <div className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-brand/5 blur-3xl" />
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="font-mono text-xs uppercase tracking-[0.35em] text-brand"
      >
        Authorised Bath Fittings Showroom · Faridabad
      </motion.p>
      <h1 className="mt-6 max-w-4xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl" data-testid="brands-headline">
        {["Pinnacle engineering,", "architectural grace."].map((line, i) => (
          <span key={line} className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.215, 0.61, 0.355, 1] }}
            >
              {i === 1 ? <em className="text-brand-gradient not-italic">{line}</em> : line}
            </motion.span>
          </span>
        ))}
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}
        className="mt-8 max-w-xl text-base leading-relaxed text-[#5C6570] sm:text-lg"
      >
        Every brand on our floor comes through official channels — genuine billing,
        full manufacturer warranty, and live displays you can try before you buy.
      </motion.p>
    </div>
  </section>
);

export default function Brands() {
  usePageMeta(
    "Bath Fittings Brands in Faridabad | Jaquar, Kohler, Hindware, Kajaria — SRK Bath and Tiles",
    "Explore Jaquar, Kohler, Hindware bath fittings and Kajaria, Somany, Nitco designer tiles at Shri Radhey Krishna Bath and Tiles, Sector 86, Greater Faridabad. Authorised stock, project pricing for architects & developers."
  );
  return (
    <div data-testid="brands-page">
      <PageHero />
      <Marquee />

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl space-y-20 px-4 sm:px-8 lg:space-y-32 lg:px-16">
          {BRANDS.map((brand, i) => (
            <div
              key={brand.id}
              data-testid={`brand-section-${brand.id}`}
              className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16"
            >
              <Reveal className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="img-spotlight group relative overflow-hidden border border-ink/10">
                  <img
                    src={brand.image}
                    alt={`${brand.name} collection at SRK Bath and Tiles Faridabad`}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                </div>
              </Reveal>

              <Reveal delay={0.15} className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="flex flex-wrap items-center gap-3">
                  {brand.logos.map((l) => (
                    <LogoPill key={l.name} name={l.name} logo={l.logo} />
                  ))}
                </div>
                <span className="mt-6 block font-serif text-6xl font-medium text-brand/25">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                  {brand.name}
                </h2>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-brand">{brand.tagline}</p>
                <p className="mt-6 text-sm leading-relaxed text-[#5C6570] sm:text-base">{brand.blurb}</p>
                <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {brand.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2.5 text-sm text-[#3D4856]">
                      <BadgeCheck size={15} className="shrink-0 text-brand" /> {h}
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  {BRAND_DETAILS[brand.id] && (
                    <Link
                      to={`/brands/${brand.id}`}
                      data-testid={`brand-view-${brand.id}`}
                      className="group inline-flex items-center gap-3 bg-brand px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-all duration-300 hover:bg-brand-bright"
                    >
                      View {brand.name} Collection
                      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  )}
                  <Link
                    to={`/contact?brand=${encodeURIComponent(brand.name)}`}
                    data-testid={`brand-enquire-${brand.id}`}
                    className="group inline-flex items-center gap-3 border border-brand/60 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-brand transition-all duration-300 hover:bg-brand hover:text-paper"
                  >
                    Enquire about {brand.name.split(" ")[0]}
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-cream">
        <BrandWall />
      </div>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <Reveal>
            <Eyebrow>Bathroom Fittings in Faridabad</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl">
              The brands Faridabad searches for, <em className="text-brand-gradient not-italic">on one floor.</em>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#5C6570]">
              Shri Radhey Krishna Bath and Tiles is an authorised Jaquar, Kohler and
              Hindware showroom in Sector 86, Greater Faridabad — faucets, sanitaryware,
              rain showers, intelligent toilets, imported basins and designer vanities,
              backed by genuine GST billing and full manufacturer warranty.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#5C6570]">
              Alongside fittings, our floor carries Kajaria, Somany, Nitco and Kalinga
              Stone tiles, Astral and Fusion pipes, Kent RO and Zero B water systems, and
              Laticrete-grade tile adhesives — everything a bathroom project needs, from
              one counter.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-8">
          <Reveal>
            <Eyebrow light>Can't Decide?</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-medium tracking-tight text-[#F6F1E7] sm:text-3xl lg:text-4xl">
              Let us pair the right brand <em className="not-italic text-[#E8694F]">to your budget.</em>
            </h2>
            <Link
              to="/contact"
              data-testid="brands-consult-button"
              className="group mt-10 inline-flex items-center gap-3 bg-brand px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-all duration-300 hover:bg-brand-bright"
            >
              Book Free Consultation <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
